import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ActionButton from "../../components/common/buttons/ActionButton";
import { Box, Chip, IconButton } from "@mui/material";
import { Button, Card, message, Popconfirm, Tooltip } from "antd";
import { TranslationOutlined } from "@ant-design/icons";
import { useRef, useState } from "react";
import axiosInstance from "../../axios/axios-instance";
import AddChildModal from "../../components/category/AddChildModal";
import MuiTable from "../../components/common/MuiTable";
import { pathAPI } from "../../utils/pathApi";
import { useTranslation } from "react-i18next";
import { Column } from "../../utils/constants";
import TranslationModal from "../../components/common/TranslationModal";
import { useLanguages } from "../../hooks/useLanguages";
import api from "../../hooks/api";

interface CategoryChildRow extends CategoryChild {
  action: number;
}

interface TranslationData {
  translation_id: number | null;
  translation_name: string;
  language_id: number;
  language_code: string;
  language_name?: string;
}

interface CategoryTranslationsResponse {
  data?: {
    translations?: TranslationData[];
  };
}

const CategoryChildren: React.FC<{
  data: CategoryChild[];
  loading: boolean;
  mutate: () => void;
}> = ({ data, loading, mutate }) => {
  const { t } = useTranslation();
  const [{ data: languages }] = useLanguages();
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [select, setSelect] = useState<CategoryChild | null>(null);
  const [translationChild, setTranslationChild] = useState<CategoryChild | null>(null);
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [translations, setTranslations] = useState<TranslationData[]>([]);
  const [loadingTranslations, setLoadingTranslations] = useState<number | null>(null);
  const translationRequestRef = useRef(0);
  const childRows: CategoryChildRow[] = data.map((item) => ({
    ...item,
    action: item.id,
  }));

  const fetchTranslations = async (child: CategoryChild) => {
    const requestId = translationRequestRef.current + 1;
    translationRequestRef.current = requestId;

    try {
      setLoadingTranslations(child.id);
      setTranslationModalOpen(false);
      setTranslationChild(child);
      setTranslations([]);
      const response = await api.GET<CategoryTranslationsResponse>(
        `${pathAPI.categoryDetail}/${child.id}/translations`,
        {},
        {},
        true
      );
      if (translationRequestRef.current !== requestId) return;
      setTranslations(response?.data?.translations || []);
      setTranslationModalOpen(true);
    } catch (error) {
      if (translationRequestRef.current !== requestId) return;
      console.error("Error fetching child category translations:", error);
      message.error(t("category.translations_load_failed"));
    } finally {
      if (translationRequestRef.current === requestId) {
        setLoadingTranslations(null);
      }
    }
  };

  const handleTranslationSuccess = () => {
    mutate();
  };

  const header: Column[] = [
    {
      id: "id",
      label: `${t("common.table.id")}`,
      minWidth: 10,
      align: "center",
    },
    {
      id: "name",
      label: `${t("common.table.name")}`,
      minWidth: 150,
      align: "center",
      format: (value, row) => {
        const child = row as CategoryChildRow;
        return (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={1}
            flexWrap={"wrap"}
          >
            <div className="font-bold ">{value}</div>
            <Tooltip title={t("category.manage_translations")}>
              <Button
                size="small"
                icon={<TranslationOutlined />}
                aria-label={t("category.manage_translations")}
                loading={loadingTranslations === child.id}
                onClick={() => fetchTranslations(child)}
              />
            </Tooltip>
          </Box>
        );
      },
    },
    {
      id: "active",
      label: `${t("common.table.active")}`,
      minWidth: 20,
      align: "center",
      format: (value: any) => {
        return (
          <Chip
            size="small"
            variant="outlined"
            color={value ? "success" : "error"}
            label={`${value}`}
          />
        );
      },
    },
    {
      id: "action",
      label: `${t("common.table.actions")}`,
      minWidth: 50,
      align: "center",
      format: (id: number) => {
        const selectedChild = childRows.find((catChild) => catChild.id === id);
        return (
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={2}
          >
            <Box
              size={"small"}
              component={IconButton}
              disabled={loadingDelete}
              onClick={() => {
                setSelect(selectedChild || null);
                setOpenModal(true);
              }}
            >
              <EditIcon color="success" />
            </Box>
            <Popconfirm
              title={t("help_support.delete_text")}
              okText={t("btn.confirm")}
              cancelText={t("btn.cancel")}
              onConfirm={() => handleDelete(id)}
              okButtonProps={{
                style: { background: "#ba2156", border: "none" },
                loading: loadingDelete,
              }}
            >
              <Box size={"small"} component={IconButton}>
                <DeleteIcon color={"error"} />
              </Box>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  const deleteSuccess = () => {
    messageApi.open({
      type: "success",
      content: t("category.messages.delete_success"),
    });
  };

  const deleteError = () => {
    messageApi.open({
      type: "error",
      content: t("category.messages.error"),
    });
  };

  const handleDelete = async (id: number) => {
    try {
      setLoadingDelete(true);
      await axiosInstance.delete(`${pathAPI.categoryDetail}/${id}`);
      deleteSuccess();
      mutate();
    } catch (error) {
      deleteError();
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <Card className="user">
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box fontWeight={"bold"} fontSize={"16px"} mb={2}>
          {t("category.children_title")}
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          sx={{ mb: 3 }}
          onClick={() => {
            setSelect(null); // Explicitly set selected item to null for new category
            setOpenModal(true);
          }}
        >
          {t("category.add_new")}
        </ActionButton>
      </Box>
      {contextHolder}
      <MuiTable header={header} data={childRows} isLoading={loading}></MuiTable>
      <AddChildModal
        mutate={mutate}
        onCancel={() => {
          setOpenModal(false);
          setSelect(null);
        }}
        item={select}
        open={openModal}
      />
      <TranslationModal
        key={translationChild?.id || "child-translations"}
        open={translationModalOpen}
        onCancel={() => setTranslationModalOpen(false)}
        onSuccess={handleTranslationSuccess}
        entityId={translationChild?.id || ""}
        entityType="category"
        translations={translations}
        languages={languages || []}
        title={t("category.children_translations_title")}
        entityName={translationChild?.name}
      />
    </Card>
  );
};

export default CategoryChildren;
